/**
 * Routes (docs/design/06 §5): `/` (sign in, or the Play hub), `/play` (deck picker and matchmaking,
 * `?deck=` from Runebound's "Play this deck"), `/r/:code` (private room), `/match/:id` (the board),
 * `/replay/:id` and `/replay/s/:token` (replays), `/settings`, `/auth/handoff` (one-tap sign-in from
 * Runebound, Q11), `/coverage` (card coverage) and `/sandbox` (developer hot-seat). Board pages are
 * code-split; the lobby is in the main chunk.
 */
import { createRootRoute, createRoute, createRouter, lazyRouteComponent, Outlet } from '@tanstack/react-router';
import { AppShell } from './components/AppShell';
import { NotFound } from './routes/NotFound';
import { HandoffPage } from './routes/Handoff';
import { HomePage } from './routes/Home';
import { PlayPage } from './routes/Play';
import { RoomPage } from './routes/Room';
import { SettingsPage } from './routes/Settings';

/** Search params of `/play`: the deck to pre-select (Runebound "Play this deck", 07 §7). */
export interface PlaySearch {
  deck?: string;
}

function parsePlaySearch(search: Record<string, unknown>): PlaySearch {
  const deck = typeof search.deck === 'string' && search.deck.length <= 64 ? search.deck : undefined;
  return deck ? { deck } : {};
}

const rootRoute = createRootRoute({ component: Outlet, notFoundComponent: NotFound });

const shell = createRoute({
  getParentRoute: () => rootRoute,
  id: 'shell',
  component: () => (
    <AppShell>
      <Outlet />
    </AppShell>
  ),
});

const homeRoute = createRoute({
  getParentRoute: () => shell,
  path: '/',
  component: HomePage,
  validateSearch: parsePlaySearch,
});
const playRoute = createRoute({
  getParentRoute: () => shell,
  path: '/play',
  component: PlayPage,
  validateSearch: parsePlaySearch,
});
const roomRoute = createRoute({ getParentRoute: () => shell, path: '/r/$code', component: RoomPage });
const settingsRoute = createRoute({ getParentRoute: () => shell, path: '/settings', component: SettingsPage });
const coverageRoute = createRoute({
  getParentRoute: () => shell,
  path: '/coverage',
  component: lazyRouteComponent(() => import('./routes/Coverage'), 'CoveragePage'),
});
const handoffRoute = createRoute({ getParentRoute: () => shell, path: '/auth/handoff', component: HandoffPage });

const matchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/match/$id',
  component: lazyRouteComponent(() => import('./routes/Match'), 'MatchPage'),
});
const replayRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/replay/$id',
  component: lazyRouteComponent(() => import('./routes/Replay'), 'ReplayPage'),
});
const sharedReplayRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/replay/s/$token',
  component: lazyRouteComponent(() => import('./routes/Replay'), 'SharedReplayPage'),
});
const sandboxRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sandbox',
  component: lazyRouteComponent(() => import('./routes/Sandbox'), 'SandboxPage'),
});

const routeTree = rootRoute.addChildren([
  shell.addChildren([homeRoute, playRoute, roomRoute, settingsRoute, coverageRoute, handoffRoute]),
  matchRoute,
  replayRoute,
  sharedReplayRoute,
  sandboxRoute,
]);

/** The app router. */
export const router = createRouter({ routeTree, defaultPreload: 'intent', scrollRestoration: true });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
